import {
  Box,
  Button,
  Flex,
  H2,
  H4,
  Icon,
  MaterialTableWrapper,
  MaterialTableWrapperProps,
  MRT_ColumnDef,
  MRT_TableOptions,
  OverflowTooltip,
  RowActionButton,
  Tooltip,
  withThemeProvider,
} from '@ouster/webviz-react-components';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { TbArrowBackUp } from 'react-icons/tb';

import useChakraHook from '../../hooks/useChakraHook';
import { useStoreActions, useStoreState } from '../../reducers';
import { BASE_ADMIN_DASHBOARD_API } from '../../utils/constants';
import { fetchWithAuthV2 } from '../../utils/requests';
import { isAdminLevelRole } from '../../utils/roles';

type CopiedRecording = {
  copy_drive_id: number;
  copy_processed_at: string;
  recording_title: string;
  source_drive_id: number;
  source_org_id: number;
  source_org_name: string;
  target_org_id: number;
  target_org_name: string;
  copied_by_email?: string | null;
};

type CopyAuditResponse = {
  copies: CopiedRecording[];
  total: number;
};

const ThemedMaterialTableWrapper =
  withThemeProvider<MaterialTableWrapperProps<CopiedRecording>>(
    MaterialTableWrapper
  );

const formatProcessedAt = (value: string) =>
  new Date(value).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

const renderOverflowCell = (value: string, dataTestId: string) => (
  <OverflowTooltip
    value={value}
    dataTestId={dataTestId}
    style={{ width: '100%' }}
  />
);

export default function CopiedRecordings() {
  const { showToast } = useChakraHook();
  const { curUserOrg, appUser } = useStoreState(
    (storeState) => storeState.user
  );
  const { openConfirmDialog } = useStoreActions(
    (actions) => actions.confirmDialog
  );
  const [copies, setCopies] = useState<CopiedRecording[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshingCopies, setIsRefreshingCopies] = useState(false);

  const loadCopiedRecordings = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = (await fetchWithAuthV2(
        `${BASE_ADMIN_DASHBOARD_API}/copy_audit`,
        {
          method: 'GET',
          fallbackErrorMessage: 'Failed to load copied recordings audit',
        }
      )) as CopyAuditResponse;

      setCopies(response.copies);
      setTotal(response.total);
    } catch (error) {
      setCopies([]);
      setTotal(0);
      showToast({
        title: 'Failed to load copied recordings',
        description: `${error}`,
        status: 'error',
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadCopiedRecordings().catch(() => {
      // loadCopiedRecordings handles its own errors
    });
  }, [loadCopiedRecordings]);

  const onRefreshCopiedRecordings = useCallback(async () => {
    setIsRefreshingCopies(true);
    try {
      await loadCopiedRecordings();
    } finally {
      setIsRefreshingCopies(false);
    }
  }, [loadCopiedRecordings]);

  const sortedCopies = useMemo(
    () =>
      [...copies].sort(
        (left, right) =>
          new Date(right.copy_processed_at).getTime() -
          new Date(left.copy_processed_at).getTime()
      ),
    [copies]
  );

  const columns = useMemo<MRT_ColumnDef<CopiedRecording>[]>(() => {
    const baseColumns: MRT_ColumnDef<CopiedRecording>[] = [
      {
        accessorKey: 'copy_processed_at',
        header: 'Processed At',
        size: 180,
        minSize: 160,
        grow: false,
        Cell: ({ row }) => formatProcessedAt(row.original.copy_processed_at),
      },
      {
        accessorKey: 'recording_title',
        header: 'Recording',
        minSize: 260,
        size: 320,
        grow: false,
        Cell: ({ cell }) =>
          renderOverflowCell(
            cell.getValue<string>(),
            'copied-recordings-recording-title'
          ),
      },
      {
        accessorKey: 'source_org_name',
        header: 'Source Org',
        minSize: 160,
        size: 180,
        grow: false,
        Cell: ({ cell }) =>
          renderOverflowCell(
            cell.getValue<string>(),
            'copied-recordings-source-org'
          ),
      },
      {
        accessorKey: 'target_org_name',
        header: 'Target Org',
        minSize: 160,
        size: 180,
        grow: false,
        Cell: ({ cell }) =>
          renderOverflowCell(
            cell.getValue<string>(),
            'copied-recordings-target-org'
          ),
      },
      {
        accessorKey: 'copied_by_email',
        header: 'Copy Initiator',
        minSize: 180,
        size: 220,
        grow: false,
        Cell: ({ row }) =>
          renderOverflowCell(
            row.original.copied_by_email ?? '',
            'copied-recordings-initiator'
          ),
      },
    ];

    return baseColumns;
  }, []);

  const handleRevertCopy = useCallback(
    (copy: CopiedRecording) => {
      openConfirmDialog({
        title: 'Revert copy',
        body: 'Are you sure you want to recall copy of this recording from this organization?',
        details: [
          { label: 'Recording', value: copy.recording_title },
          { label: 'Organization', value: copy.target_org_name },
        ],
        confirmLabel: 'Revert copy',
        isDestructive: true,
        onConfirm: async () => {
          try {
            await fetchWithAuthV2(
              `${BASE_ADMIN_DASHBOARD_API}/copy_audit/${copy.copy_drive_id}`,
              {
                method: 'DELETE',
                fallbackErrorMessage: 'Failed to revert copied recording',
              }
            );

            await loadCopiedRecordings();

            showToast({
              title: 'Copy reverted',
              description: 'Copy reverted successfully.',
              status: 'success',
            });
          } catch (error) {
            showToast({
              title: 'Failed to revert copy',
              description: `${error}`,
              status: 'error',
            });
            throw error;
          }
        },
      });
    },
    [loadCopiedRecordings, openConfirmDialog, showToast]
  );

  const openPrivateVizPage = useCallback(
    (copy: CopiedRecording) => {
      if (isLoading || !copy.source_drive_id) return;
      window.open(
        `/organization/1/drive/${copy.source_drive_id}`,
        '_blank',
        'noopener,noreferrer'
      );
    },
    [isLoading]
  );

  const canRevertCopy = useCallback(
    (copy: CopiedRecording) => {
      if (isAdminLevelRole(curUserOrg?.role)) {
        return true;
      }

      const isSupportUser = curUserOrg?.role === 'SUPPORT';
      if (!isSupportUser) {
        return false;
      }

      const currentUserEmail = appUser?.email?.trim().toLowerCase();
      const initiatorEmail = copy.copied_by_email?.trim().toLowerCase();

      return Boolean(
        currentUserEmail &&
          initiatorEmail &&
          currentUserEmail === initiatorEmail
      );
    },
    [appUser?.email, curUserOrg?.role]
  );

  const tableOptions: MRT_TableOptions<CopiedRecording> = useMemo(
    () => ({
      columns,
      data: sortedCopies,
      initialState: {
        pagination: { pageSize: 50, pageIndex: 0 },
        columnPinning: {
          right: ['mrt-row-actions'],
          left: ['copy_processed_at'],
        },
        showGlobalFilter: true,
      },
      enableRowActions: true,
      enableSorting: true,
      enableToolbarInternalActions: false,
      positionActionsColumn: 'last',
      positionGlobalFilter: 'right',
      muiTablePaperProps: {
        sx: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
      },
      muiTableContainerProps: {
        sx: {
          border: '1px solid',
          borderColor: 'rgba(255, 255, 255, 0.12)',
          maxHeight: 'none',
          overflowY: 'visible',
        },
      },
      muiTableHeadCellProps: ({ column }) => ({
        sx: {},
        'data-testid': `header-${column.id}`,
      }),
      muiTableBodyCellProps: ({ column }) => {
        const constrainedColumns = new Set([
          'copied_by_email',
          'recording_title',
          'source_org_name',
          'target_org_name',
        ]);

        if (column.id === 'mrt-row-actions') {
          return {
            'data-testid': `row-${column.id}`,
            style: {
              whiteSpace: 'nowrap',
            },
          };
        }

        if (constrainedColumns.has(column.id)) {
          const maxWidthByColumn: Record<string, string> = {
            copied_by_email: '220px',
            recording_title: '320px',
            source_org_name: '180px',
            target_org_name: '180px',
          };

          return {
            'data-testid': `row-${column.id}`,
            style: {
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: maxWidthByColumn[column.id],
            },
          };
        }

        return {
          'data-testid': `row-${column.id}`,
        };
      },
      muiPaginationProps: {
        rowsPerPageOptions: [25, 50, 100, 200],
      },
      getRowId: (originalRow) =>
        `${originalRow.copy_drive_id}-${originalRow.target_org_id}`,
      renderRowActions: ({ row }) => {
        const canRevert = canRevertCopy(row.original);

        return (
          <Box flexWrap="nowrap" display="flex" alignItems="center">
            <RowActionButton<CopiedRecording>
              title="Open source recording private link"
              icon={<Icon icon="openInNew" />}
              row={row.original}
              onClickHandler={openPrivateVizPage}
            />
            <Tooltip
              label={
                canRevert ? undefined : 'Requires admin or copy initiator.'
              }
            >
              <span>
                <RowActionButton<CopiedRecording>
                  title="Revert copy"
                  icon={<TbArrowBackUp />}
                  row={row.original}
                  onClickHandler={handleRevertCopy}
                  isDisabled={!canRevert}
                />
              </span>
            </Tooltip>
          </Box>
        );
      },
      state: {
        isLoading,
        showLoadingOverlay: false,
        showAlertBanner: false,
        showSkeletons: isLoading,
      },
    }),
    [
      canRevertCopy,
      columns,
      handleRevertCopy,
      isLoading,
      openPrivateVizPage,
      sortedCopies,
    ]
  );

  return (
    <Box
      h="full"
      p="25px 36px 0px"
      display="flex"
      flexDirection="column"
      gap={4}
    >
      <Flex gap={4} alignItems="center" justifyContent="space-between">
        <H2 fontWeight="medium" fontSize={30}>
          Copied Recordings
        </H2>
        <Button
          onClick={onRefreshCopiedRecordings}
          variant="outline"
          minWidth="min-content"
          data-testid="refresh-copied-recordings-button"
          leftIcon="refresh"
          disabled={isRefreshingCopies}
        >
          Refresh List
        </Button>
      </Flex>
      <Box position="relative" h="full">
        <H4
          color="gray.300"
          fontSize={14}
          position="absolute"
          left={0}
          top={4}
          zIndex={2}
          display="flex"
          alignItems="center"
          gap={2}
        >
          <Box
            borderRadius="full"
            w={2}
            h={2}
            bg="green.300"
            display="inline-block"
            aria-hidden="true"
          />
          <span style={{ lineHeight: 1 }}>
            Total copied recording entries: {total}
          </span>
        </H4>
        <Box zIndex={1}>
          <ThemedMaterialTableWrapper {...tableOptions} />
        </Box>
      </Box>
    </Box>
  );
}
